import React, { useState, useEffect, useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { LoadingState } from '../../components/common/LoadingState';
import getDataTableStyles from '../../utils/tableStyles';
import { Alert, Button, Spinner, Modal, Form, InputGroup, Badge, Table, Tabs, Tab, Row, Col, Card } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api/axios';

const getCustomStyles = (isDark) => ({
  headCells: {
    style: {
      backgroundColor: '#183eeb', // Rutex Corporate Primary Blue
      color: 'white',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      padding: '15px',
      fontSize: '0.82rem',
      border: 'none',
    },
  },
  rows: {
    style: {
      backgroundColor: isDark ? '#161e38' : '#ffffff',
      color: isDark ? '#ffffff' : '#010028',
      paddingTop: '6px',
      paddingBottom: '6px',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(24, 62, 235, 0.04)',
      },
    },
  },
  cells: {
    style: {
      border: 'none',
      color: isDark ? '#ffffff' : '#010028',
    },
  },
  table: {
    style: {
      backgroundColor: isDark ? '#161e38' : '#ffffff',
      borderCollapse: 'collapse',
      border: 'none',
    },
  },
  tableWrapper: {
    style: {
      backgroundColor: isDark ? '#161e38' : '#ffffff',
    },
  },
  progress: {
    style: {
      backgroundColor: isDark ? '#161e38' : '#ffffff',
      color: '#818cf8',
    },
  },
  pagination: {
    style: {
      backgroundColor: isDark ? '#161e38' : '#ffffff',
      color: isDark ? '#ffffff' : '#010028',
    },
  },
  noData: {
    style: {
      backgroundColor: isDark ? '#161e38' : '#ffffff',
      color: isDark ? '#94a3b8' : '#64748b',
    },
  },
});

function Orders() {
  const { t } = useTranslation();
  const { isDark } = useContext(ThemeContext) || {};
  const customStyles = getDataTableStyles(isDark);

  // Navigation tabs state ('pending', 'weighing', 'history')
  const [activeTab, setActiveTab] = useState('pending');

  // Orders State
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingAction, setUpdatingAction] = useState({ id: null, action: null });

  const [bcvRate, setBcvRate] = useState(0);
  const [loadingBcvRate, setLoadingBcvRate] = useState(true);
  const [statusCatalog, setStatusCatalog] = useState({ estatus_pedido: [], estatus_factura: [] });
  const [zones, setZones] = useState([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const [showDetails, setShowDetails] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [showWeighingModal, setShowWeighingModal] = useState(false);
  const [weighingOrder, setWeighingOrder] = useState(null);
  const [editedWeights, setEditedWeights] = useState({});
  const [savingWeights, setSavingWeights] = useState(false);

  const [showProformaModal, setShowProformaModal] = useState(false);
  const [proformaOrder, setProformaOrder] = useState(null);
  const [proformaItems, setProformaItems] = useState([]);
  const [customBcvRate, setCustomBcvRate] = useState(0);
  const [systemIvaRate, setSystemIvaRate] = useState(0);
  const [savingProforma, setSavingProforma] = useState(false);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectOrder, setRejectOrder] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [savingReject, setSavingReject] = useState(false);

  // Voucher / Receipt Lightbox State
  const [previewVoucher, setPreviewVoucher] = useState({ show: false, url: '', reference: '', paymentMethod: '', payment: null, order: null });
  const [copiedRef, setCopiedRef] = useState(false);

  // Payment Rejection Modal State (HU27 / Cobranza)
  const [rejectPaymentModal, setRejectPaymentModal] = useState({ show: false, order: null, payment: null });
  const [rejectReasonsCatalog, setRejectReasonsCatalog] = useState([]);
  const [loadingRejectReasons, setLoadingRejectReasons] = useState(false);
  const [selectedRejectReasons, setSelectedRejectReasons] = useState([]);
  const [rejectObservations, setRejectObservations] = useState('');
  const [savingRejectPayment, setSavingRejectPayment] = useState(false);

  const fetchRejectReasonsCatalog = async () => {
    try {
      setLoadingRejectReasons(true);
      const res = await axiosInstance.get('/core/masters/payment_rejection_reasons');
      if (res.data?.success && Array.isArray(res.data?.data)) {
        setRejectReasonsCatalog(res.data.data.filter(r => r.is_active));
      }
    } catch (err) {
      console.error('Error al cargar catálogo de motivos de rechazo de pago', err);
    } finally {
      setLoadingRejectReasons(false);
    }
  };

  const openRejectPaymentModal = (order, payment = null) => {
    if (rejectReasonsCatalog.length === 0) {
      fetchRejectReasonsCatalog();
    }
    setSelectedRejectReasons([]);
    setRejectObservations('');
    setRejectPaymentModal({
      show: true,
      order: order,
      payment: payment || (order?.payments && order.payments.length > 0 ? order.payments[0] : null),
    });
  };

  const closeRejectPaymentModal = () => {
    setRejectPaymentModal({ show: false, order: null, payment: null });
    setSelectedRejectReasons([]);
    setRejectObservations('');
  };

  const toggleRejectReason = (value) => {
    setSelectedRejectReasons(prev => 
      prev.includes(value) ? prev.filter(r => r !== value) : [...prev, value]
    );
  };

  const handleConfirmRejectPayment = async (e) => {
    if (e) e.preventDefault();
    if (selectedRejectReasons.length === 0) {
      alert('Por favor selecciona al menos un motivo de rechazo.');
      return;
    }

    const { order, payment } = rejectPaymentModal;
    if (!order) return;

    setSavingRejectPayment(true);
    try {
      const payload = {
        motivos: selectedRejectReasons,
        observaciones: rejectObservations,
      };
      if (payment?.id) {
        payload.payment_id = payment.id;
      }

      const res = await axiosInstance.post(`/sales/orders/${order.id}/reject-payment`, payload);
      alert(res.data?.message || 'Comprobante de pago rechazado exitosamente. Se ha notificado al cliente.');
      
      closeRejectPaymentModal();
      
      // If the voucher modal is open for this order, update its payment badge immediately
      if (previewVoucher.show && (previewVoucher.order?.id === order.id || previewVoucher.payment?.pedido_id === order.id)) {
        setPreviewVoucher(prev => ({
          ...prev,
          payment: prev.payment ? { ...prev.payment, estatus: 'Rechazado' } : null
        }));
      }

      // Refresh orders list
      fetchOrders(false);
    } catch (err) {
      handleConcurrencyError(err, 'Error al rechazar el pago');
    } finally {
      setSavingRejectPayment(false);
    }
  };

  const openVoucherModal = (paymentOrUrl, reference = '', paymentMethod = '', order = null) => {
    if (paymentOrUrl && typeof paymentOrUrl === 'object') {
      const url = paymentOrUrl.comprobante_url || paymentOrUrl.evidencia_url || '';
      const ref = paymentOrUrl.referencia || reference || '';
      const method = paymentOrUrl.metodo_pago || paymentMethod || '';
      setPreviewVoucher({ show: true, url, reference: ref, paymentMethod: method, payment: paymentOrUrl, order: order || paymentOrUrl.order || null });
    } else {
      setPreviewVoucher({ show: true, url: paymentOrUrl || '', reference, paymentMethod, payment: null, order: order || null });
    }
  };

  const closeVoucherModal = () => {
    setPreviewVoucher({ show: false, url: '', reference: '', paymentMethod: '', payment: null, order: null });
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedRef(true);
    setTimeout(() => setCopiedRef(false), 2000);
  };

  // Fetch BCV Rate
  const fetchBcvRate = async () => {
    try {
      setLoadingBcvRate(true);
      const res = await axiosInstance.get(`/sales/bcv-rate?_t=${Date.now()}`);
      if (res.data?.success && res.data?.data?.rate && parseFloat(res.data.data.rate) > 0) {
        const rateVal = parseFloat(res.data.data.rate);
        setBcvRate(rateVal);
        setCustomBcvRate(rateVal);
      }
    } catch (err) {
      console.error('Error al consultar Tasa BCV', err);
    } finally {
      setLoadingBcvRate(false);
    }
  };

  // Fetch dynamic status catalog from Backend API
  const fetchStatusCatalog = async () => {
    try {
      const res = await axiosInstance.get('/sales/orders/statuses');
      if (res.data.success) {
        setStatusCatalog(res.data.data);
      }
    } catch (err) {
      console.error('Error al cargar catálogo de estados desde backend', err);
    }
  };

  // Fetch dynamic zones catalog from Backend API
  const fetchZones = async () => {
    try {
      const res = await axiosInstance.get('/admin/zones');
      if (res.data?.success && Array.isArray(res.data?.data)) {
        setZones(res.data.data);
      }
    } catch (err) {
      console.error('Error al cargar catálogo de zonas', err);
    }
  };

  // Fetch core global settings (IVA percentage)
  const fetchCoreSettings = async () => {
    try {
      const res = await axiosInstance.get('/core/settings');
      if (res.data?.data?.iva_porcentaje !== undefined) {
        setSystemIvaRate(parseFloat(res.data.data.iva_porcentaje) || 0);
      }
    } catch (err) {
      console.error('Error al cargar configuración global', err);
    }
  };

  // Fetch orders from backend with optional silent refresh
  const fetchOrders = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError('');
      }
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (zoneFilter) params.append('zone_id', zoneFilter);
      if (dateFilter) params.append('date', dateFilter);

      const response = await axiosInstance.get(`/sales/orders?${params.toString()}`);
      if (response.data.success) {
        setOrders(response.data.data || []);
      } else if (!silent) {
        setError('Error al cargar órdenes desde el servidor.');
      }
    } catch (err) {
      if (!silent) {
        setError('Ocurrió un error al conectar con el servidor.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchStatusCatalog();
    fetchBcvRate();
    fetchZones();
    fetchCoreSettings();
  }, []);

  useEffect(() => {
    fetchOrders(false);

    // Concurrency Guard: Auto-refetch when user focuses/switches to this window
    const handleWindowFocus = () => {
      fetchOrders(true);
    };
    window.addEventListener('focus', handleWindowFocus);

    // Concurrency Guard: Background polling every 15s to keep split screens synchronized
    const pollInterval = setInterval(() => {
      fetchOrders(true);
    }, 15000);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      clearInterval(pollInterval);
    };
  }, [search, zoneFilter, dateFilter, activeTab]);

  // Concurrency Error Handler Helper
  const handleConcurrencyError = (err, defaultMsg) => {
    if (err.response?.status === 409 || err.response?.data?.error_code === 'ORDER_STATUS_CONFLICT') {
      alert('⚠️ ' + (err.response?.data?.message || 'El pedido fue modificado o procesado por otro usuario. Sincronizando consola...'));
      fetchOrders(false);
      return true;
    }
    alert(defaultMsg + ': ' + (err.response?.data?.message || err.message));
    return false;
  };

  // Status translation helpers
  const getOrderStatusLabel = (ep) => {
    if (!ep) return '';
    if (ep === 'En Despacho' || ep === 'in_transit') return t('status_in_dispatch', 'En Despacho');
    if (ep === 'En Pesaje' || ep === 'in_warehouse') return t('status_in_weighing', 'En Pesaje');
    if (ep === 'Facturado') return t('status_invoiced', 'Facturado');
    if (ep === 'Aprobado') return t('status_approved', 'Aprobado');
    if (ep === 'Entregado' || ep === 'delivered') return t('status_delivered', 'Entregado');
    if (ep === 'Completado' || ep === 'completed') return t('status_completed', 'Completado');
    if (ep === 'Despachado') return t('status_dispatched', 'Despachado');
    if (ep === 'Cancelado' || ep === 'cancelled') return t('status_cancelled', 'Cancelado');
    if (ep === 'Por Pagar') return t('status_for_payment', 'Por Pagar');
    if (ep === 'En Verificación') return t('status_in_verification', 'En Verificación');
    return ep;
  };

  const getInvoiceStatusLabel = (f) => {
    if (!f) return t('status_not_enabled', 'No Habilitado');
    if (f === 'Pendiente por Cobrar' || f === 'Por Pagar' || f === 'Pendiente de Pago') return t('pending_payment', 'Pendiente por Cobrar');
    if (f === 'En Verificación') return t('status_in_verification', 'En Verificación');
    if (f === 'Aprobado' || f === 'Pagada' || f === 'Pagado' || f === 'Validado') return t('status_approved', 'Aprobado');
    if (f === 'Rechazado') return t('status_rejected', 'Rechazado');
    if (f === 'Vencido') return t('status_expired', 'Vencido');
    if (f === 'Anulada' || f === 'Cancelado') return t('status_cancelled', 'Cancelado');
    return f;
  };

  // Tab 0 Data: Orders for Proforma Approval (HU27)
  const forApprovalOrders = orders.filter(o => {
    const ep = o.estatus_pedido || o.estatus;
    return ep === 'Pendiente por Revisar' || ep === 'Recibido' || ep === 'pending_approval';
  });

  // Tab 1 Data: Approved and incoming orders pending warehouse weighing
  const pendingOrders = orders.filter(o => {
    const ep = o.estatus_pedido || o.estatus;
    return (
      ep === 'Pendiente por Revisar' || 
      ep === 'Recibido' || 
      ep === 'pending_approval' || 
      ep === 'Aprobado' || 
      ep === 'Pendiente de Pesaje' || 
      o.estatus === 'Pendiente de Pesaje'
    ) && !o.hoja_pesaje_impresa_at;
  });

  // Tab 2 Data: Currently in weighing in warehouse
  const weighingOrders = orders.filter(o => {
    const ep = o.estatus_pedido || o.estatus;
    return ep === 'En Almacén / Pesaje' || ep === 'En Pesaje' || ep === 'in_warehouse';
  });

  // Tab 3 Data: History, billing, dispatch and collections
  const historyOrders = orders.filter(o => {
    const ep = o.estatus_pedido || o.estatus;
    const ef = o.estatus_factura || o.estatus;
    return (
      ep === 'Facturado / Pre-Facturado' || 
      ep === 'billed' || 
      ep === 'Facturado' || 
      ep === 'Pesado' ||
      ep === 'En Despacho / En Ruta' || 
      ep === 'in_transit' || 
      ep === 'En Preparación' ||
      ep === 'Entregado en Comercio' || 
      ep === 'delivered' || 
      ep === 'Despachado' ||
      ep === 'Cerrado / Liquidado' || 
      ep === 'closed' || 
      ep === 'Cancelado / Rechazado' || 
      ep === 'cancelled' || 
      ep === 'Cancelado' ||
      ef === 'Pendiente por Cobrar' || 
      ef === 'pending' || 
      ef === 'Por Pagar' || 
      ef === 'En Verificación (Por Conciliar)' || 
      ef === 'submitted' || 
      ef === 'En Verificación' || 
      ef === 'Aprobado / Conciliado' || 
      ef === 'approved' || 
      ef === 'Pagada' || 
      ef === 'Pagado' ||
      ef === 'Rechazado (Comprobante Inválido)' || 
      ef === 'rejected' ||
      ef === 'Factura Vencida / Moroso' || 
      ef === 'overdue' ||
      o.estatus === 'Por Pagar' ||
      o.estatus === 'Verificando Pago' ||
      o.estatus === 'Despachado' ||
      o.estatus === 'Cancelado'
    );
  }).filter(o => !statusFilter || (o.estatus_factura === statusFilter || o.estatus_pedido === statusFilter || o.estatus === statusFilter));

  // Toggle order checkbox selection in Tab 1
  const toggleSelectOrder = (id) => {
    if (selectedOrderIds.includes(id)) {
      setSelectedOrderIds(selectedOrderIds.filter(i => i !== id));
    } else {
      setSelectedOrderIds([...selectedOrderIds, id]);
    }
  };

  const selectAllPending = () => {
    if (selectedOrderIds.length === pendingOrders.length) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(pendingOrders.map(o => o.id));
    }
  };

  // Open Order Details Modal
  const openOrderDetails = async (orderId) => {
    try {
      setLoadingDetails(true);
      setShowDetails(true);
      setSelectedOrder(null);
      const res = await axiosInstance.get(`/sales/orders/${orderId}`);
      if (res.data.success) {
        setSelectedOrder(res.data.data);
      }
    } catch (err) {
      alert('Error al cargar el detalle del pedido.');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Open Proforma Approval Modal (HU27 - FE-01)
  const openProformaModal = (order) => {
    setProformaOrder(order);
    const rateToUse = order.tasa_cambio && parseFloat(order.tasa_cambio) > 1 ? parseFloat(order.tasa_cambio) : bcvRate;
    setCustomBcvRate(rateToUse);
    setProformaItems(
      (order.items || []).map(i => ({
        id: i.id,
        sku: i.product?.sku || i.producto?.sku || 'N/A',
        nombre: i.product?.nombre || i.producto?.nombre || 'Producto',
        cantidad: parseFloat(i.cantidad_solicitada || i.cantidad || 1),
        precio_unitario_usd: parseFloat(i.precio_unitario_usd || 0),
      }))
    );
    setShowProformaModal(true);
  };

  const handleProformaItemChange = (id, field, value) => {
    setProformaItems(prev =>
      prev.map(item => item.id === id ? { ...item, [field]: parseFloat(value) || 0 } : item)
    );
  };

  const calculateProformaTotals = () => {
    let subtotal = 0;
    proformaItems.forEach(i => {
      subtotal += (i.cantidad * i.precio_unitario_usd);
    });
    const iva = systemIvaRate > 0 ? (subtotal * (systemIvaRate / 100)) : 0;
    const totalUsd = subtotal + iva;
    const totalVes = totalUsd * customBcvRate;
    return { subtotal, iva, totalUsd, totalVes };
  };

  const handleApproveProformaSubmit = async () => {
    if (!proformaOrder) return;
    setSavingProforma(true);
    try {
      const payload = {
        tasa_cambio: customBcvRate,
        items: proformaItems.map(i => ({
          id: i.id,
          cantidad: i.cantidad,
          precio_unitario_usd: i.precio_unitario_usd,
        }))
      };
      const res = await axiosInstance.post(`/sales/orders/${proformaOrder.id}/approve-proforma`, payload);
      if (res.data.success) {
        alert(res.data.message || t('proforma_approved_success', 'Proforma emitida con éxito.'));
        const targetId = proformaOrder.id;
        setShowProformaModal(false);
        fetchOrders();
        handleViewProformaPdf(targetId);
      }
    } catch (err) {
      handleConcurrencyError(err, 'Error al aprobar y generar la proforma');
      setShowProformaModal(false);
    } finally {
      setSavingProforma(false);
    }
  };

  // Open Rejection Modal (HU27 - FE-02)
  const openRejectModal = (order) => {
    setRejectOrder(order);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleRejectProformaSubmit = async () => {
    if (!rejectOrder) return;
    if (!rejectReason.trim()) {
      alert('Por favor ingresa la justificación obligatoria para el rechazo.');
      return;
    }
    setSavingReject(true);
    try {
      const res = await axiosInstance.post(`/sales/orders/${rejectOrder.id}/reject-proforma`, {
        motivo: rejectReason
      });
      if (res.data.success) {
        alert(res.data.message || t('order_rejected_stock_released', 'Pedido rechazado e inventario devuelto.'));
        setShowRejectModal(false);
        if (showDetails) setShowDetails(false);
        fetchOrders();
      }
    } catch (err) {
      handleConcurrencyError(err, 'Error al rechazar el pedido');
      setShowRejectModal(false);
    } finally {
      setSavingReject(false);
    }
  };

  // View Proforma PDF via authenticated axios blob stream
  const handleViewProformaPdf = async (orderId) => {
    try {
      const response = await axiosInstance.get(`/sales/orders/${orderId}/proforma-pdf`, {
        responseType: 'blob'
      });
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    } catch (err) {
      alert('Error al descargar o visualizar la Factura Proforma en PDF. ' + (err.response?.data?.message || ''));
    }
  };

  // Consolidate & Generate ALPINCA Weighing/Load Sheet via DomPDF
  const handleConsolidateAndPrint = async (singleOrder = null) => {
    const targetIds = singleOrder ? [singleOrder.id] : selectedOrderIds;
    if (targetIds.length === 0) {
      alert('Por favor selecciona al menos un pedido para consolidar.');
      return;
    }

    try {
      const response = await axiosInstance.get(`/sales/orders/weighing-pdf?ids=${targetIds.join(',')}`, {
        responseType: 'blob'
      });

      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');

      setSelectedOrderIds([]);
      fetchOrders();
    } catch (err) {
      handleConcurrencyError(err, 'Error al generar la hoja de pesaje en PDF');
    }
  };

  // Open Weighing Modal
  const openWeighingModal = (order) => {
    setWeighingOrder(order);
    const initialWeights = {};
    order.items?.forEach(item => {
      initialWeights[item.id] = item.cantidad_pesada !== null ? item.cantidad_pesada : (item.cantidad_solicitada || item.cantidad);
    });
    setEditedWeights(initialWeights);
    setShowWeighingModal(true);
  };

  const handleWeightInputChange = (itemId, val) => {
    setEditedWeights({ ...editedWeights, [itemId]: val });
  };

  const saveWeighedAmounts = async () => {
    if (!weighingOrder) return;
    setSavingWeights(true);
    try {
      const itemsPayload = Object.keys(editedWeights).map(id => ({
        id,
        cantidad_pesada: parseFloat(editedWeights[id]) || 0
      }));

      const res = await axiosInstance.post(`/sales/orders/${weighingOrder.id}/confirm-weighing`, {
        items: itemsPayload
      });

      if (res.data.success) {
        alert(res.data.message || 'Pesaje guardado y total recalculado.');
        setShowWeighingModal(false);
        fetchOrders();
      }
    } catch (err) {
      handleConcurrencyError(err, 'Error al registrar pesaje');
      setShowWeighingModal(false);
    } finally {
      setSavingWeights(false);
    }
  };

  // Update Status in History Tab
  const updateStatus = async (id, newStatus) => {
    try {
      setUpdatingAction({ id, action: newStatus });
      const response = await axiosInstance.patch(`/sales/orders/${id}/status`, { status: newStatus });
      if (response.data.success) {
        fetchOrders();
      }
    } catch (err) {
      handleConcurrencyError(err, 'Error al actualizar estatus');
    } finally {
      setUpdatingAction({ id: null, action: null });
    }
  };

  // HU08 [FE-01]: Helper visual para identificar pedidos creados bajo Venta Asistida (Impersonation)
  const renderOrderNumber = (row) => (
    <div className="d-flex flex-column py-1">
      <strong>{row.numero_pedido}</strong>
      {row.is_impersonated && (
        <div>
          <Badge 
            bg="info" 
            className="text-white d-inline-flex align-items-center mt-1" 
            style={{ fontSize: '0.68rem', padding: '2px 5px', fontWeight: '500' }}
            title={row.impersonated_vendedor_nombre ? `Venta Asistida - Creado por Vendedor: ${row.impersonated_vendedor_nombre}` : 'Pedido por Venta Asistida'}
          >
            <i className="mdi mdi-account-switch me-1"></i>Venta Asistida
          </Badge>
        </div>
      )}
    </div>
  );

  const renderCustomerCell = (row) => (
    <div className="py-1" style={{ maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
      <div className="fw-medium text-dark text-break" style={{ lineHeight: '1.25' }}>{row.cliente ? row.cliente.razon_social : 'N/A'}</div>
      {row.is_impersonated && row.impersonated_vendedor_nombre && (
        <small className="text-muted d-block text-break mt-1" style={{ fontSize: '0.72rem' }}>
          <i className="mdi mdi-account-tie me-1 text-info"></i>
          Por: <span className="fw-semibold text-dark">{row.impersonated_vendedor_nombre}</span>
        </small>
      )}
    </div>
  );

  // Columns for Tab 0 (Por Aprobar - Proforma HU27)
  const forApprovalColumns = [
    {
      name: t('th_order_id', 'NRO PEDIDO'),
      selector: row => row.numero_pedido,
      sortable: true,
      width: '145px',
      cell: renderOrderNumber
    },
    {
      name: t('th_customer', 'CLIENTE'),
      selector: row => row.cliente ? row.cliente.razon_social : 'N/A',
      sortable: true,
      cell: renderCustomerCell
    },
    {
      name: t('th_estimated_total', 'TOTAL REF ($)'),
      selector: row => `$${parseFloat(row.total_referencial_usd || row.total_base_usd || 0).toFixed(2)}`,
      sortable: true,
      width: '120px',
      cell: row => <span className="fw-bold text-dark">${parseFloat(row.total_referencial_usd || row.total_base_usd || 0).toFixed(2)}</span>
    },
    {
      name: t('th_date', 'FECHA CREACIÓN'),
      selector: row => row.created_at,
      sortable: true,
      width: '140px',
      cell: row => <small className="text-muted">{new Date(row.created_at).toLocaleDateString()}</small>
    },
    {
      name: t('th_actions', 'ACCIONES'),
      width: '320px',
      cell: row => (
        <div className="d-flex gap-1 py-1">
          <Button 
            variant="success" 
            size="sm" 
            onClick={() => openProformaModal(row)}
            style={{ borderRadius: '6px', fontSize: '0.78rem', padding: '4px 10px' }}
            className="fw-bold shadow-sm"
          >
            <i className="mdi mdi-file-document-edit-outline me-1"></i>
            {t('btn_approve_proforma', 'Aprobar & Proforma')}
          </Button>

          <Button 
            variant="outline-danger" 
            size="sm" 
            onClick={() => openRejectModal(row)}
            style={{ borderRadius: '6px', fontSize: '0.78rem', padding: '4px 8px' }}
          >
            <i className="mdi mdi-close-circle-outline me-1"></i>
            {t('btn_reject_order', 'Rechazar')}
          </Button>

          <Button 
            variant="outline-info" 
            size="sm" 
            onClick={() => openOrderDetails(row.id)}
            style={{ borderRadius: '6px', fontSize: '0.78rem', padding: '4px 8px' }}
          >
            {t('btn_detail', 'Detalle')}
          </Button>
        </div>
      )
    }
  ];

  // Columns for Tab 1 (Pending Weighing)
  const pendingColumns = [
    {
      name: (
        <div className="d-flex align-items-center justify-content-center w-100 py-1">
          <input 
            type="checkbox"
            className="form-check-input order-select-header-checkbox m-0 cursor-pointer"
            checked={pendingOrders.length > 0 && selectedOrderIds.length === pendingOrders.length}
            onChange={selectAllPending}
            title={selectedOrderIds.length === pendingOrders.length ? t('deselect_all', 'Deseleccionar todos') : t('select_all', 'Seleccionar todos')}
          />
        </div>
      ),
      width: '56px',
      cell: row => {
        const isSelected = selectedOrderIds.includes(row.id);
        return (
          <div className="d-flex align-items-center justify-content-center w-100 py-1">
            <input 
              type="checkbox"
              className={`form-check-input order-select-checkbox m-0 cursor-pointer ${isSelected ? 'is-selected' : ''}`}
              checked={isSelected}
              onChange={() => toggleSelectOrder(row.id)}
              title={isSelected ? t('deselect_order', 'Deseleccionar pedido') : t('select_order', 'Seleccionar pedido')}
            />
          </div>
        );
      }
    },
    {
      name: t('th_order_id', 'NRO PEDIDO'),
      selector: row => row.numero_pedido,
      sortable: true,
      width: '145px',
      cell: renderOrderNumber
    },
    {
      name: t('th_customer', 'CLIENTE'),
      selector: row => row.cliente ? row.cliente.razon_social : 'N/A',
      sortable: true,
      grow: 2,
      minWidth: '200px',
      cell: renderCustomerCell
    },
    {
      name: t('th_zone', 'ZONA'),
      selector: row => row.cliente?.zone?.name || row.cliente?.zone?.code || t('no_zone', 'Sin Zona'),
      sortable: true,
      width: '180px',
      cell: row => {
        const zoneName = row.cliente?.zone?.name || row.cliente?.zone?.code;
        return zoneName ? (
          <Badge 
            bg="primary" 
            className="fw-normal text-wrap text-start text-break" 
            style={{ fontSize: '0.78rem', lineHeight: '1.25', maxWidth: '100%' }}
            title={zoneName}
          >
            {zoneName}
          </Badge>
        ) : (
          <Badge bg="secondary" className="fw-normal" style={{ fontSize: '0.78rem' }}>{t('no_zone', 'Sin Zona')}</Badge>
        );
      }
    },
    {
      name: t('th_real_total', 'TOTAL REF ($)'),
      selector: row => `$${parseFloat(row.total_referencial_usd || row.total_base_usd || 0).toFixed(2)}`,
      sortable: true,
      width: '135px',
      cell: row => <strong className="fw-bold">${parseFloat(row.total_referencial_usd || row.total_base_usd || 0).toFixed(2)}</strong>
    },
    {
      name: t('th_actions', 'ACCIONES'),
      width: '180px',
      cell: row => {
        const isSelected = selectedOrderIds.includes(row.id);
        return (
          <div className="d-flex flex-column gap-1 py-1 w-100">
            <Button 
              variant="outline-info" 
              size="sm" 
              onClick={() => openOrderDetails(row.id)}
              style={{ borderRadius: '6px', whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 8px' }}
              title={t('btn_detail')}
            >
              <i className="mdi mdi-eye-outline me-1"></i>
              {t('btn_detail')}
            </Button>

            <span 
              className="d-inline-block w-100"
              title={isSelected 
                ? t('tooltip_order_already_selected', "Este pedido ya está incluido en la selección. Usa el botón superior 'Consolidar e Imprimir'") 
                : t('tooltip_consolidate_single', 'Consolidar y generar hoja de pesaje para este pedido')
              }
              style={{ cursor: isSelected ? 'not-allowed' : 'pointer' }}
            >
              <Button 
                variant="primary" 
                size="sm" 
                disabled={isSelected}
                onClick={() => handleConsolidateAndPrint(row)}
                className="w-100"
                style={{ 
                  borderRadius: '6px', 
                  whiteSpace: 'nowrap', 
                  fontSize: '0.78rem', 
                  padding: '3px 8px', 
                  fontWeight: '600',
                  opacity: isSelected ? 0.55 : 1,
                  pointerEvents: isSelected ? 'none' : 'auto'
                }}
              >
                <i className="mdi mdi-layers-triple me-1"></i>
                {t('btn_consolidate', 'Consolidar')}
              </Button>
            </span>
          </div>
        );
      }
    }
  ];

  // Highlight selected rows in Tab 1
  const pendingRowStyles = [
    {
      when: row => selectedOrderIds.includes(row.id),
      style: {
        backgroundColor: isDark ? 'rgba(24, 62, 235, 0.18)' : '#eff6ff',
        borderLeft: '4px solid #183eeb',
        '&:hover': {
          backgroundColor: isDark ? 'rgba(24, 62, 235, 0.32)' : '#dbeafe',
          borderLeft: '4px solid #183eeb',
        },
      },
      classNames: ['row-selected'],
    },
  ];

  // Columns for Tab 2 (Weighing)
  const weighingColumns = [
    {
      name: t('th_order_id', 'NRO PEDIDO'),
      selector: row => row.numero_pedido,
      sortable: true,
      width: '145px',
      cell: renderOrderNumber
    },
    {
      name: t('th_customer', 'CLIENTE'),
      selector: row => row.cliente ? row.cliente.razon_social : 'N/A',
      sortable: true,
      grow: 2,
      minWidth: '200px',
      cell: renderCustomerCell
    },
    {
      name: t('th_zone', 'ZONA'),
      selector: row => row.cliente?.zone?.name || row.cliente?.zone?.code || t('no_zone', 'Sin Zona'),
      sortable: true,
      width: '180px',
      cell: row => {
        const zoneName = row.cliente?.zone?.name || row.cliente?.zone?.code;
        return zoneName ? (
          <Badge 
            bg="primary" 
            className="fw-normal text-wrap text-start text-break" 
            style={{ fontSize: '0.78rem', lineHeight: '1.25', maxWidth: '100%' }}
            title={zoneName}
          >
            {zoneName}
          </Badge>
        ) : (
          <Badge bg="secondary" className="fw-normal" style={{ fontSize: '0.78rem' }}>{t('no_zone', 'Sin Zona')}</Badge>
        );
      }
    },
    {
      name: t('th_actions', 'ACCIONES'),
      width: '180px',
      cell: row => (
        <div className="d-flex flex-column gap-1 py-1 w-100">
          <Button 
            variant="outline-info" 
            size="sm" 
            onClick={() => openOrderDetails(row.id)}
            style={{ borderRadius: '6px', whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 8px' }}
            title={t('btn_detail')}
          >
            {t('btn_detail')}
          </Button>

          <Button 
            variant="info" 
            size="sm" 
            onClick={() => openWeighingModal(row)}
            style={{ borderRadius: '6px', whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 8px', color: 'white' }}
          >
            {t('btn_real_kilos')}
          </Button>
        </div>
      )
    }
  ];

  // Columns for Tab 3 (History)
  const historyColumns = [
    {
      name: t('th_order_id', 'NRO PEDIDO'),
      selector: row => row.numero_pedido,
      sortable: true,
      width: '145px',
      cell: renderOrderNumber
    },
    {
      name: t('th_customer', 'CLIENTE'),
      selector: row => row.cliente ? row.cliente.razon_social : 'N/A',
      sortable: true,
      grow: 2,
      minWidth: '200px',
      cell: renderCustomerCell
    },
    {
      name: t('th_zone', 'ZONA'),
      selector: row => row.cliente?.zone?.name || row.cliente?.zone?.code || t('no_zone', 'Sin Zona'),
      sortable: true,
      width: '180px',
      cell: row => {
        const zoneName = row.cliente?.zone?.name || row.cliente?.zone?.code;
        return zoneName ? (
          <Badge 
            bg="primary" 
            className="fw-normal text-wrap text-start text-break" 
            style={{ fontSize: '0.78rem', lineHeight: '1.25', maxWidth: '100%' }}
            title={zoneName}
          >
            {zoneName}
          </Badge>
        ) : (
          <Badge bg="secondary" className="fw-normal" style={{ fontSize: '0.78rem' }}>{t('no_zone', 'Sin Zona')}</Badge>
        );
      }
    },
    {
      name: t('th_total_order', 'TOTAL FACTURA ($)'),
      selector: row => `$${parseFloat(row.total_definitivo_usd > 0 ? row.total_definitivo_usd : (row.total_referencial_usd || row.total_base_usd || 0)).toFixed(2)}`,
      sortable: true,
      width: '140px',
      cell: row => <strong className="fw-bold">${parseFloat(row.total_definitivo_usd > 0 ? row.total_definitivo_usd : (row.total_referencial_usd || row.total_base_usd || 0)).toFixed(2)}</strong>
    },
    {
      name: t('th_order_status', 'ESTATUS PEDIDO'),
      width: '150px',
      cell: row => {
        const ep = row.estatus_pedido || row.estatus;
        let color = 'secondary';
        if (ep === 'En Pesaje' || ep === 'in_warehouse') color = 'info';
        if (ep === 'Facturado' || ep === 'Aprobado') color = 'primary';
        if (ep === 'En Despacho' || ep === 'in_transit') color = 'warning';
        if (ep === 'Entregado' || ep === 'delivered') color = 'info';
        if (ep === 'Completado' || ep === 'Despachado' || ep === 'completed') color = 'success';
        if (ep === 'Cancelado' || ep === 'cancelled') color = 'danger';
        return <Badge bg={color}>{getOrderStatusLabel(ep)}</Badge>;
      }
    },
    {
      name: t('th_invoice_status', 'ESTATUS COBRANZA'),
      width: '165px',
      cell: row => {
        const f = row.estatus_factura || row.estatus;
        const activePayment = row.payments && row.payments.length > 0 ? row.payments[0] : null;

        let badgeColor = 'secondary';
        let badgeText = undefined;
        if (f === 'Pendiente por Cobrar' || f === 'Por Pagar' || f === 'Pendiente de Pago') {
          badgeColor = 'warning';
          badgeText = 'dark';
        } else if (f === 'En Verificación') {
          badgeColor = 'info';
        } else if (f === 'Aprobado' || f === 'Pagada' || f === 'Pagado') {
          badgeColor = 'success';
        } else if (f === 'Rechazado' || f === 'Vencido' || f === 'Anulada' || f === 'Cancelado') {
          badgeColor = 'danger';
        }

        return (
          <div className="d-flex flex-column align-items-start gap-1">
            <Badge bg={badgeColor} text={badgeText}>{getInvoiceStatusLabel(f)}</Badge>

            {activePayment?.referencia && (
              <small className="text-muted font-monospace" style={{ fontSize: '0.72rem' }} title="Número de Referencia">
                Ref: {activePayment.referencia}
              </small>
            )}
          </div>
        );
      }
    },
    {
      name: t('th_credit_status', 'CRÉDITO'),
      width: '145px',
      cell: row => {
        const ef = row.estatus_factura || row.estatus;
        const isPaid = ef === 'Aprobado' || ef === 'Pagada' || ef === 'Pagado';
        const isCancelled = ef === 'Cancelado' || row.estatus === 'Cancelado';

        if (isPaid) {
          return <Badge bg="light" text="dark" className="border" style={{ fontSize: '0.72rem' }}><i className="mdi mdi-check-circle text-success me-1"></i>{t('credit_paid', 'Pagada')}</Badge>;
        }
        if (isCancelled) {
          return <Badge bg="light" text="dark" className="border" style={{ fontSize: '0.72rem' }}>—</Badge>;
        }
        if (!row.fecha_entrega || row.dias_credito_restantes === null || row.dias_credito_restantes === undefined) {
          return <Badge bg="light" text="muted" className="border" style={{ fontSize: '0.72rem' }}>{t('credit_not_delivered', 'Sin entregar')}</Badge>;
        }

        if (row.is_overdue) {
          return <Badge bg="danger" style={{ fontSize: '0.72rem' }}><i className="mdi mdi-alert-circle me-1"></i>{t('credit_overdue', 'Vencida')} (+{row.dias_vencidos}d)</Badge>;
        }
        if (row.dias_credito_restantes === 0) {
          return <Badge bg="warning" text="dark" style={{ fontSize: '0.72rem' }}><i className="mdi mdi-clock-alert me-1"></i>{t('credit_due_today', 'Vence hoy')}</Badge>;
        }
        if (row.dias_credito_restantes <= 2) {
          return <Badge bg="warning" text="dark" style={{ fontSize: '0.72rem' }}><i className="mdi mdi-clock-outline me-1"></i>{row.dias_credito_restantes}d {t('credit_remaining', 'restante(s)')}</Badge>;
        }
        return <Badge bg="success" style={{ fontSize: '0.72rem' }}><i className="mdi mdi-clock-check me-1"></i>{row.dias_credito_restantes}d</Badge>;
      }
    },
    {
      name: t('th_actions', 'ACCIONES'),
      width: '180px',
      cell: row => {
        const ep = row.estatus_pedido || row.estatus;
        const ef = row.estatus_factura || row.estatus;

        // Solo se puede despachar si está Facturado o Pesado y aún NO ha salido a despacho
        const canDispatch = (ep === 'Facturado' || ep === 'Pesado' || ep === 'billed' || ep === 'En Preparación') &&
                            ep !== 'En Despacho' && ep !== 'in_transit' && ep !== 'Entregado' && ep !== 'delivered' &&
                            ep !== 'Completado' && ep !== 'closed' && ep !== 'Cancelado' && ep !== 'cancelled' &&
                            row.estatus !== 'Despachado';

        // Si ya está en despacho, se puede confirmar entrega
        const canMarkDelivered = (ep === 'En Despacho' || ep === 'in_transit') && 
                                 ep !== 'Entregado' && ep !== 'delivered' && ep !== 'Completado';

        // Solo se puede aprobar pago SI el cliente ya envió el comprobante (En Verificación)
        const canApprovePayment = (ef === 'En Verificación' || ef === 'submitted' || row.estatus === 'Verificando Pago') &&
                                  ef !== 'Aprobado' && ef !== 'approved' && ef !== 'Pagado' && ef !== 'Pagada';

        const isRowUpdating = updatingAction.id === row.id;
        const activePayment = row.payments && row.payments.length > 0 ? row.payments[0] : null;

        return (
          <div className="d-flex flex-column gap-1 py-1 w-100">
            <Button 
              variant="outline-info" 
              size="sm" 
              disabled={isRowUpdating}
              onClick={() => openOrderDetails(row.id)}
              style={{ borderRadius: '6px', whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 8px' }}
              title={t('btn_detail')}
            >
              {t('btn_detail')}
            </Button>

            {(activePayment?.comprobante_url || activePayment?.evidencia_url) && (
              <Button 
                variant="outline-warning" 
                size="sm" 
                disabled={isRowUpdating}
                onClick={() => openVoucherModal(activePayment, '', '', row)}
                style={{ borderRadius: '6px', whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 8px', color: '#b45309', borderColor: '#f59e0b' }}
                title={t('tooltip_view_voucher', 'Ver Comprobante de Pago adjunto')}
              >
                <i className="mdi mdi-receipt-text-outline me-1"></i>
                {t('btn_voucher', 'Comprobante')}
              </Button>
            )}

            <Button 
              variant="outline-secondary" 
              size="sm" 
              disabled={isRowUpdating}
              onClick={() => handleViewProformaPdf(row.id)}
              style={{ borderRadius: '6px', whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 8px' }}
              title={t('tooltip_proforma_pdf', 'Ver / Descargar Factura Proforma PDF')}
            >
              <i className="mdi mdi-file-pdf-box me-1"></i>
              Proforma PDF
            </Button>

            {canDispatch && (
              <Button 
                variant="outline-primary" 
                size="sm" 
                disabled={isRowUpdating}
                onClick={() => updateStatus(row.id, 'Despachado')}
                style={{ borderRadius: '6px', whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 8px' }}
              >
                {isRowUpdating && updatingAction.action === 'Despachado' ? <Spinner size="sm" animation="border" /> : t('btn_dispatch', 'Despachar')}
              </Button>
            )}

            {canMarkDelivered && (
              <Button 
                variant="outline-info" 
                size="sm" 
                disabled={isRowUpdating}
                onClick={() => updateStatus(row.id, 'Entregado')}
                style={{ borderRadius: '6px', whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 8px' }}
              >
                {isRowUpdating && updatingAction.action === 'Entregado' ? <Spinner size="sm" animation="border" /> : t('btn_mark_delivered', 'Marcar Entregado')}
              </Button>
            )}

            {canApprovePayment && (
              <Button 
                variant="outline-success" 
                size="sm" 
                disabled={isRowUpdating}
                onClick={() => updateStatus(row.id, 'Aprobado')}
                style={{ borderRadius: '6px', whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '3px 8px' }}
              >
                {isRowUpdating && updatingAction.action === 'Aprobado' ? <Spinner size="sm" animation="border" /> : t('btn_approve_payment', 'Aprobar Pago')}
              </Button>
            )}
          </div>
        );
      }
    }
  ];

  const totalsSummary = calculateProformaTotals();

  return (
    <div>
      {/* Header */}
      <div className='page-header d-flex justify-content-between align-items-center'>
        <div>
          <h3 className='page-title'>{t('orders_console_title')}</h3>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item"><a href="!#" onClick={e => e.preventDefault()}>Ventas</a></li>
              <li className="breadcrumb-item active" aria-current="page">Órdenes, Proforma y Logística</li>
            </ol>
          </nav>
        </div>

        {/* BCV Exchange Rate Live Badge */}
        <div className="bg-white px-3 py-2 rounded shadow-sm border border-primary d-flex align-items-center">
          <i className="mdi mdi-currency-usd text-primary me-2" style={{ fontSize: '1.4rem' }}></i>
          <div>
            <div className="small text-muted fw-bold" style={{ fontSize: '0.75rem' }}>{t('bcv_rate_badge', 'Tasa Oficial BCV del Día:')}</div>
            <div className="fw-bold text-dark" style={{ fontSize: '1.05rem', color: '#00024f' }}>
              {loadingBcvRate ? (
                <span className="text-muted small">{t('bcv_rate_loading', 'Cargando tasa...')}</span>
              ) : bcvRate > 0 ? (
                `Bs. ${parseFloat(bcvRate).toFixed(4)} / $`
              ) : (
                <span className="text-muted small">{t('bcv_rate_unavailable', 'Tasa no disponible')}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filter Bar */}
      <Card className="shadow-sm mb-4" style={{ borderRadius: '12px', border: 'none' }}>
        <Card.Body>
          <Row className="g-3 align-items-center">
            <Col md={4}>
              <Form.Label className="small fw-bold text-muted mb-1">{t('general_search', 'Búsqueda General')}</Form.Label>
              <Form.Control 
                placeholder={t('search_order_placeholder', 'Buscar por Nro Pedido, RIF o Cliente...')} 
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="customer-filter-control w-100 shadow-none"
              />
            </Col>

            <Col md={4}>
              <Form.Label className="small fw-bold text-muted mb-1">{t('filter_by_zone', 'Filtro por Zona')}</Form.Label>
              <Form.Select 
                value={zoneFilter} 
                onChange={e => setZoneFilter(e.target.value)}
                className="customer-filter-control w-100 cursor-pointer shadow-none"
              >
                <option value="">{t('all_zones', 'Todas las Zonas')}</option>
                {zones.map(z => (
                  <option key={z.id} value={z.id}>
                    {z.name} {z.code ? `(${z.code})` : ''}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={4}>
              <Form.Label className="small fw-bold text-muted mb-1">{t('date_range', 'Rango de Fecha')}</Form.Label>
              <Form.Control 
                type="date"
                value={dateFilter}
                onChange={e => setDateFilter(e.target.value)}
                className="customer-filter-control w-100 shadow-none"
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 3 Main Operational Tabs */}
      <Tabs activeKey={activeTab} onSelect={k => setActiveTab(k)} className="minimal-underline-tabs mb-4">

        {/* TAB 1: PENDIENTES POR PESAJE */}
        <Tab eventKey="pending" title={<>{t('tab_pending')} <Badge bg="primary" className="ms-1">{pendingOrders.length}</Badge></>}>
          <Card className="shadow-sm" style={{ borderRadius: '12px', border: 'none' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h5 className="mb-0 text-primary fw-bold">{t('tab_pending_header', 'Pedidos Aprobados Pendientes de Pesaje')}</h5>
                <Button 
                  variant={selectedOrderIds.length > 0 ? "primary" : "outline-primary"} 
                  disabled={selectedOrderIds.length === 0}
                  onClick={() => handleConsolidateAndPrint()}
                  style={{ 
                    borderRadius: '8px', 
                    fontWeight: 'bold',
                    fontSize: '0.85rem',
                    padding: '7px 16px',
                    opacity: selectedOrderIds.length === 0 ? 0.65 : 1,
                    borderWidth: '2px',
                    cursor: selectedOrderIds.length === 0 ? 'not-allowed' : 'pointer'
                  }}
                  title={selectedOrderIds.length === 0 ? t('tooltip_select_orders_to_consolidate', 'Selecciona uno o más pedidos en la tabla para consolidar') : t('btn_consolidate_print', 'Consolidar e Imprimir')}
                >
                  <i className="mdi mdi-layers-triple me-1"></i>
                  {t('btn_consolidate_print', 'Consolidar e Imprimir')} {selectedOrderIds.length > 0 ? `(${selectedOrderIds.length})` : ''}
                </Button>
              </div>

              <div className="table-responsive" style={{ width: '100%', overflowX: 'auto' }}>
                <DataTable 
                  columns={pendingColumns} 
                  data={pendingOrders} 
                  pagination 
                  responsive
                  customStyles={customStyles} 
                  conditionalRowStyles={pendingRowStyles}
                  progressPending={loading}
                  progressComponent={<LoadingState title={t('loading_orders', 'Cargando pedidos...')} description={t('loading_orders_sub', 'Consultando despachos, pesajes y estados financieros')} />}
                  noDataComponent={<div className="my-5 text-muted">{t('no_orders_pending_weighing', 'No hay pedidos aprobados pendientes de pesaje en la bandeja de entrada.')}</div>}
                />
              </div>
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 2: EN PESAJE EN ALMACEN */}
        <Tab eventKey="weighing" title={<>{t('tab_weighing')} <Badge bg="info" className="ms-1">{weighingOrders.length}</Badge></>}>
          <Card className="shadow-sm" style={{ borderRadius: '12px', border: 'none' }}>
            <Card.Body>
              <h5 className="mb-3 text-info fw-bold">{t('tab_weighing_header', 'Pedidos en Proceso de Pesaje y Báscula')}</h5>
              <div className="table-responsive" style={{ width: '100%', overflowX: 'auto' }}>
                <DataTable 
                  columns={weighingColumns} 
                  data={weighingOrders} 
                  pagination 
                  responsive
                  customStyles={customStyles} 
                  progressPending={loading}
                  progressComponent={<LoadingState title={t('loading_orders', 'Cargando pedidos...')} description={t('loading_orders_sub', 'Consultando despachos, pesajes y estados financieros')} />}
                  noDataComponent={<div className="my-5 text-muted">{t('no_orders_pending_weighing', 'No hay pedidos en proceso de pesaje en este momento.')}</div>}
                />
              </div>
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 3: HISTORIAL Y COBRANZA */}
        <Tab eventKey="history" title={<>{t('tab_history')} <Badge bg="secondary" className="ms-1">{historyOrders.length}</Badge></>}>
          <Card className="shadow-sm" style={{ borderRadius: '12px', border: 'none' }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 text-dark fw-bold">{t('tab_history_header', 'Historial de Pedidos, Facturación y Cobranzas')}</h5>
                <Form.Select 
                  value={statusFilter} 
                  onChange={e => setStatusFilter(e.target.value)}
                  style={{ width: '220px', border: '1px solid #183eeb', borderRadius: '8px' }}
                  size="sm"
                >
                  <option value="">{t('status_all', 'Todos los Estados')}</option>
                  {statusCatalog.estatus_factura?.map(st => (
                    <option key={st.id} value={st.nombre}>{st.nombre}</option>
                  ))}
                </Form.Select>
              </div>

              <div className="table-responsive" style={{ width: '100%', overflowX: 'auto' }}>
                <DataTable 
                  columns={historyColumns} 
                  data={historyOrders} 
                  pagination 
                  responsive
                  customStyles={customStyles} 
                  progressPending={loading}
                  progressComponent={<LoadingState title={t('loading_orders', 'Cargando pedidos...')} description={t('loading_orders_sub', 'Consultando despachos, pesajes y estados financieros')} />}
                  noDataComponent={<div className="my-5 text-muted">{t('no_orders_in_history', 'No hay órdenes en el historial con estos criterios.')}</div>}
                />
              </div>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* MODAL HU27: REVISIÓN Y EMISIÓN DE FACTURA PROFORMA (FE-01) */}
      <Modal show={showProformaModal} onHide={() => setShowProformaModal(false)} size="xl" centered>
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: 'white', borderBottom: '3px solid #183eeb' }}>
          <Modal.Title className="h5 fw-bold">
            <i className="mdi mdi-file-document-edit-outline me-2"></i>
            {t('modal_proforma_title', 'Revisión y Generación de Factura Proforma Virtual')} — Pedido #{proformaOrder?.numero_pedido}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
          {proformaOrder && (
            <div>
              {/* Info Header */}
              <Row className="mb-4 bg-light p-3 rounded border align-items-center">
                <Col md={12}>
                  <div className="fw-bold text-dark" style={{ fontSize: '1.05rem' }}>{proformaOrder.cliente?.razon_social}</div>
                  <div className="small text-muted">RIF: {proformaOrder.cliente?.rif_cedula || 'N/A'} | Condición: {proformaOrder.condicion_pago || 'Contado'}</div>
                </Col>
              </Row>

              <h6 className="fw-bold text-primary mb-2">{t('editable_products_table', 'Tabla de Productos Editable (Ajuste Real de Cantidades y Precios)')}</h6>
              <Table striped bordered hover responsive size="sm" className="align-middle">
                <thead style={{ backgroundColor: '#183eeb', color: 'white' }}>
                  <tr>
                    <th>SKU</th>
                    <th>{t('th_name', 'Producto')}</th>
                    <th style={{ width: '130px' }}>{t('th_requested_qty', 'Cantidad Solicitada')}</th>
                    <th style={{ width: '160px' }}>{t('th_unit_price', 'Precio Unit. ($)')}</th>
                    <th className="text-end">{t('th_subtotal', 'Subtotal USD ($)')}</th>
                  </tr>
                </thead>
                <tbody>
                  {proformaItems.map(item => {
                    const subtotal = item.cantidad * item.precio_unitario_usd;
                    return (
                      <tr key={item.id}>
                        <td><code>{item.sku}</code></td>
                        <td>{item.nombre}</td>
                        <td>
                          <Form.Control 
                            type="number" 
                            step="0.01" 
                            value={item.cantidad} 
                            onChange={e => handleProformaItemChange(item.id, 'cantidad', e.target.value)}
                            style={{ border: '1.5px solid #183eeb', fontWeight: 'bold' }}
                            size="sm"
                          />
                        </td>
                        <td>
                          <InputGroup size="sm">
                            <InputGroup.Text>$</InputGroup.Text>
                            <Form.Control 
                              type="number" 
                              step="0.01" 
                              value={item.precio_unitario_usd} 
                              onChange={e => handleProformaItemChange(item.id, 'precio_unitario_usd', e.target.value)}
                              style={{ border: '1.5px solid #183eeb', fontWeight: 'bold' }}
                            />
                          </InputGroup>
                        </td>
                        <td className="text-end fw-bold text-dark">
                          ${subtotal.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {/* Totals Summary */}
              <Row className="mt-4 pt-3 border-top justify-content-end">
                <Col md={5}>
                  <Card className="border-primary shadow-sm" style={{ backgroundColor: '#f0f4ff' }}>
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted">{t('subtotal_base_usd', 'Subtotal Base USD:')}</span>
                        <span className="fw-bold">${totalsSummary.subtotal.toFixed(2)}</span>
                      </div>
                      {systemIvaRate > 0 && (
                        <div className="d-flex justify-content-between mb-1">
                          <span className="text-muted">{t('iva_usd', `IVA (${systemIvaRate}%) USD:`)}</span>
                          <span className="fw-bold">${totalsSummary.iva.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="d-flex justify-content-between pt-2 border-top">
                        <span className="fw-bold text-dark">{t('total_referential_usd', 'TOTAL REFERENCIAL USD ($):')}</span>
                        <span className="fw-bold h5 mb-0 text-primary">${totalsSummary.totalUsd.toFixed(2)}</span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="secondary" onClick={() => setShowProformaModal(false)}>{t('btn_cancel', 'Cancelar')}</Button>
          <Button 
            variant="success" 
            onClick={handleApproveProformaSubmit} 
            disabled={savingProforma}
            className="fw-bold px-4 py-2"
            style={{ borderRadius: '10px' }}
          >
            {savingProforma ? <Spinner size="sm" animation="border" /> : <><i className="mdi mdi-check-circle me-1"></i> {t('btn_approve_proforma', 'Aprobar y Generar Proforma')}</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL HU27: RECHAZO DE PEDIDO Y LIBERACIÓN DE STOCK (FE-02) */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#dc2626', color: 'white' }}>
          <Modal.Title className="h5 fw-bold">
            <i className="mdi mdi-alert-circle-outline me-2"></i>
            {t('modal_reject_title', 'Rechazo de Pedido y Liberación de Stock')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Alert variant="warning" className="border-0 shadow-sm mb-3">
            <i className="mdi mdi-alert me-2" style={{ fontSize: '1.2rem' }}></i>
            <span>{t('stock_release_warning', '⚠️ Atención: Al rechazar este pedido, el inventario físico reservado será devuelto automáticamente al stock general.')}</span>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold text-dark">
              {t('field_rejection_reason', 'Justificación Obligatoria de Rechazo / Ajuste:')} *
            </Form.Label>
            <Form.Control 
              as="textarea"
              rows={4}
              required
              placeholder="Ejemplo: Sin existencia física disponible en almacén para despachar la cantidad solicitada..."
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              style={{ border: '2px solid #dc2626', borderRadius: '10px' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>{t('btn_cancel', 'Cancelar')}</Button>
          <Button 
            variant="danger" 
            onClick={handleRejectProformaSubmit} 
            disabled={savingReject}
            className="fw-bold"
            style={{ borderRadius: '10px' }}
          >
            {savingReject ? <Spinner size="sm" animation="border" /> : t('btn_confirm_reject', 'Confirmar Rechazo y Liberar Stock')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL DETALLES DEL PEDIDO */}
      <Modal show={showDetails} onHide={() => setShowDetails(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: 'white' }}>
          <Modal.Title>{t('modal_order_detail')} #{selectedOrder?.numero_pedido}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {loadingDetails ? (
            <div className="text-center my-4"><Spinner animation="border" variant="primary" /></div>
          ) : selectedOrder ? (
            <div>
              {/* HU08 [FE-01]: Alerta Destacada de Auditoría por Venta Asistida (Impersonation) */}
              {(selectedOrder.is_impersonated || selectedOrder.impersonated_vendedor_nombre) && (
                <Alert variant="info" className="d-flex align-items-center mb-3 shadow-sm border-0" style={{ backgroundColor: '#e0f2fe', color: '#0369a1', borderRadius: '8px' }}>
                  <i className="mdi mdi-account-switch fs-3 me-3 text-info"></i>
                  <div>
                    <div className="fw-bold fs-6">Pedido por Venta Asistida (Gestionado por Vendedor)</div>
                    <div className="small">
                      Este pedido fue procesado en nombre del cliente. <strong>Creado por el Vendedor: {selectedOrder.impersonated_vendedor_nombre || selectedOrder.vendedor?.nombre_completo || 'Vendedor Autorizado'}</strong>
                    </div>
                  </div>
                </Alert>
              )}

              <Row className="mb-3">
                <Col md={6}>
                  <h6 className="fw-bold text-primary">{t('customer_info', 'Información del Cliente')}</h6>
                  <p className="mb-1"><strong>{t('field_business_name', 'Razón Social')}:</strong> {selectedOrder.cliente?.razon_social || 'N/A'}</p>
                  <p className="mb-1"><strong>{t('field_rif_cedula', 'RIF / Cédula')}:</strong> {selectedOrder.cliente?.rif_cedula || 'N/A'}</p>
                  <p className="mb-1"><strong>{t('field_payment_terms', 'Condición de Pago')}:</strong> {selectedOrder.condicion_pago || 'Contado'}</p>
                </Col>
                <Col md={6}>
                  <h6 className="fw-bold text-primary">{t('order_status', 'Estado del Pedido')}</h6>
                  <p className="mb-1"><strong>{t('th_order_status', 'Estatus Pedido')}:</strong> <Badge bg="info">{getOrderStatusLabel(selectedOrder.estatus_pedido || selectedOrder.estatus)}</Badge></p>
                  <p className="mb-1"><strong>{t('th_invoice_status', 'Estatus Factura')}:</strong> <Badge bg="warning">{getInvoiceStatusLabel(selectedOrder.estatus_factura || selectedOrder.estatus)}</Badge></p>
                  <p className="mb-1"><strong>{t('th_date', 'Fecha de Creación')}:</strong> {new Date(selectedOrder.created_at).toLocaleString()}</p>
                  {selectedOrder.is_impersonated && (
                    <p className="mb-1">
                      <strong>{t('field_origin', 'Modalidad')}:</strong>{' '}
                      <Badge bg="info" className="text-white">
                        <i className="mdi mdi-account-switch me-1"></i>Venta Asistida (Por Vendedor)
                      </Badge>
                    </p>
                  )}
                </Col>
              </Row>

              {/* Información de Crédito y Vencimiento */}
              {selectedOrder.fecha_entrega ? (
                <Card className="mb-3 border-0 shadow-sm" style={{ backgroundColor: selectedOrder.is_overdue ? '#fff5f5' : '#f8fafd', borderRadius: '10px', borderLeft: `4px solid ${selectedOrder.is_overdue ? '#dc3545' : '#10b981'}` }}>
                  <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className={`fw-bold mb-0 d-flex align-items-center ${selectedOrder.is_overdue ? 'text-danger' : 'text-success'}`}>
                        <i className={`mdi ${selectedOrder.is_overdue ? 'mdi-alert-circle' : 'mdi-calendar-clock'} me-2 fs-5`}></i>
                        {t('credit_conditions', 'Condiciones de Crédito y Cobranza')}
                      </h6>
                      {selectedOrder.is_overdue ? (
                        <Badge bg="danger" className="text-uppercase">
                          {t('credit_overdue', 'Factura Vencida')} (+{selectedOrder.dias_vencidos}d)
                        </Badge>
                      ) : selectedOrder.dias_credito_restantes === 0 ? (
                        <Badge bg="warning" text="dark" className="text-uppercase">
                          {t('credit_due_today', 'Vence Hoy')}
                        </Badge>
                      ) : (
                        <Badge bg="success" className="text-uppercase">
                          {selectedOrder.dias_credito_restantes} {t('days_left', 'Días Restantes')}
                        </Badge>
                      )}
                    </div>
                    <Row className="g-2 small">
                      <Col sm={4}>
                        <span className="text-muted">{t('field_delivered_at', 'Fecha de Entrega')}:</span><br />
                        <strong>{new Date(selectedOrder.fecha_entrega).toLocaleDateString()}</strong>
                      </Col>
                      <Col sm={4}>
                        <span className="text-muted">{t('field_credit_days', 'Plazo Concedido')}:</span><br />
                        <strong>{selectedOrder.dias_credito ?? 8} {t('days', 'días')}</strong>
                      </Col>
                      <Col sm={4}>
                        <span className="text-muted">{t('field_due_date', 'Fecha Límite / Vencimiento')}:</span><br />
                        <strong className={selectedOrder.is_overdue ? 'text-danger' : 'text-dark'}>
                          {selectedOrder.fecha_vencimiento ? new Date(selectedOrder.fecha_vencimiento).toLocaleDateString() : 'N/A'}
                        </strong>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ) : (
                <div className="p-2 mb-3 bg-light rounded text-muted small border">
                  <i className="mdi mdi-information-outline me-1"></i>
                  {t('credit_starts_on_delivery', 'Los días de crédito se activarán y calcularán automáticamente al marcar el pedido como "Entregado".')}
                </div>
              )}

              {/* Tarjeta de Auditoría de Pago y Comprobante */}
              {(() => {
                const activePayment = selectedOrder.payments && selectedOrder.payments.length > 0 ? selectedOrder.payments[0] : (selectedOrder.payment || null);
                const ref = activePayment?.referencia || selectedOrder.referencia;
                const voucherUrl = activePayment?.evidencia_url || selectedOrder.comprobante_url;
                const method = activePayment?.metodo_pago || 'Pago Reportado';
                const amount = activePayment?.monto_reportado ?? activePayment?.monto;
                const currency = activePayment?.moneda || 'USD';
                const paymentStatus = activePayment?.estatus || selectedOrder.estatus_factura || 'Pendiente';

                if (!activePayment && !ref && !voucherUrl) return null;

                return (
                  <Card className="my-3 border-0 shadow-sm" style={{ backgroundColor: '#f0f4ff', borderRadius: '10px', borderLeft: '4px solid #183eeb' }}>
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="fw-bold text-primary mb-0 d-flex align-items-center">
                          <i className="mdi mdi-receipt-text-check-outline me-2 fs-5"></i>
                          {t('payment_voucher_received', 'Comprobante y Datos de Pago Recibido')}
                        </h6>
                        <Badge bg={paymentStatus === 'Validado' ? 'success' : paymentStatus === 'Rechazado' ? 'danger' : 'warning'} className="text-uppercase">
                          {getInvoiceStatusLabel(paymentStatus)}
                        </Badge>
                      </div>
                      <Row className="align-items-center">
                        <Col sm={voucherUrl ? 8 : 12}>
                          <div className="mb-1">
                            <span className="text-muted small">{t('payment_method', 'Método')}:</span>{' '}
                            <strong>{method}</strong>
                          </div>
                          {ref && (
                            <div className="mb-1 d-flex align-items-center flex-wrap gap-2">
                              <span className="text-muted small">{t('payment_reference', 'Referencia')}:</span>
                              <code className="px-2 py-1 bg-white rounded border fw-bold text-dark">{ref}</code>
                              <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                className="py-0 px-2 btn-sm"
                                onClick={() => copyToClipboard(ref)}
                                title="Copiar referencia"
                              >
                                <i className={`mdi ${copiedRef ? 'mdi-check text-success' : 'mdi-content-copy'}`}></i>
                                <span className="ms-1 small">{copiedRef ? '¡Copiado!' : 'Copiar'}</span>
                              </Button>
                            </div>
                          )}
                          {amount !== undefined && amount !== null && (
                            <div className="mb-1">
                              <span className="text-muted small">{t('reported_amount', 'Monto Reportado')}:</span>{' '}
                              <strong>{currency} {parseFloat(amount).toFixed(2)}</strong>
                              {activePayment?.monto_equivalente_usd && currency !== 'USD' && (
                                <span className="text-muted small ms-2">
                                  (≈ ${parseFloat(activePayment.monto_equivalente_usd).toFixed(2)} USD)
                                </span>
                              )}
                            </div>
                          )}
                          {activePayment?.tasa_cambio && (
                            <div className="mb-1">
                              <span className="text-muted small">{t('exchange_rate_applied', 'Tasa Aplicada')}:</span>{' '}
                              <span className="badge bg-light text-dark border">
                                {currency === 'VES' ? `${parseFloat(activePayment.tasa_cambio).toFixed(2)} Bs/$` : `${parseFloat(activePayment.tasa_cambio).toFixed(4)}`}
                              </span>
                            </div>
                          )}
                          {/* Detalles Segmentados por Método de Pago */}
                          {activePayment?.detalles_pago && typeof activePayment.detalles_pago === 'object' && Object.keys(activePayment.detalles_pago).length > 0 && (
                            <div className="my-2 p-2 bg-white rounded border small">
                              <div className="text-muted fw-bold mb-1" style={{ fontSize: '0.72rem' }}>
                                {t('payment_specific_details', 'Datos de Conciliación Bancaria:')}
                              </div>
                              <div className="d-flex flex-wrap gap-2">
                                {activePayment.detalles_pago.banco_origen && (
                                  <div><span className="text-muted">Banco Origen:</span> <strong>{activePayment.detalles_pago.banco_origen}</strong></div>
                                )}
                                {activePayment.detalles_pago.telefono_emisor && (
                                  <div><span className="text-muted">Teléfono Emisor:</span> <strong>{activePayment.detalles_pago.telefono_emisor}</strong></div>
                                )}
                                {activePayment.detalles_pago.cedula_emisor && (
                                  <div><span className="text-muted">Cédula/RIF:</span> <strong>{activePayment.detalles_pago.cedula_emisor}</strong></div>
                                )}
                                {activePayment.detalles_pago.titular_cuenta && (
                                  <div><span className="text-muted">Titular:</span> <strong>{activePayment.detalles_pago.titular_cuenta}</strong></div>
                                )}
                                {activePayment.detalles_pago.email_emisor && (
                                  <div><span className="text-muted">Email Zelle:</span> <strong>{activePayment.detalles_pago.email_emisor}</strong></div>
                                )}
                              </div>
                            </div>
                          )}
                          {activePayment?.created_at && (
                            <div className="text-muted small">
                              <span>{t('report_date', 'Fecha de Reporte')}:</span>{' '}
                              {new Date(activePayment.created_at).toLocaleString()}
                            </div>
                          )}
                        </Col>
                        {voucherUrl && (
                          <Col sm={4} className="text-sm-end text-center mt-2 mt-sm-0">
                            <div 
                              className="position-relative d-inline-block shadow-sm rounded overflow-hidden" 
                              style={{ cursor: 'pointer', maxWidth: '140px', border: '2px solid #183eeb' }}
                              onClick={() => openVoucherModal(activePayment || { comprobante_url: voucherUrl, referencia: ref, metodo_pago: method, monto_reportado: amount, moneda: currency }, '', '', selectedOrder)}
                              title="Click para ampliar comprobante"
                            >
                              {voucherUrl?.toLowerCase().includes('.pdf') ? (
                                <div className="d-flex flex-column align-items-center justify-content-center bg-light" style={{ width: '100%', height: '80px' }}>
                                  <i className="mdi mdi-file-pdf-box text-danger" style={{ fontSize: '32px' }}></i>
                                  <span className="small text-muted fw-bold" style={{ fontSize: '0.7rem' }}>PDF</span>
                                </div>
                              ) : (
                                <img 
                                  src={voucherUrl} 
                                  alt="Comprobante de pago" 
                                  style={{ width: '100%', height: '80px', objectFit: 'cover' }} 
                                  onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/140x80?text=Comprobante'; }}
                                />
                              )}
                              <div 
                                className="position-absolute bottom-0 start-0 w-100 text-center py-1 text-white small fw-bold" 
                                style={{ backgroundColor: 'rgba(24, 62, 235, 0.85)', fontSize: '0.75rem' }}
                              >
                                <i className="mdi mdi-magnify-plus-outline me-1"></i> Ver Comprobante
                              </div>
                            </div>
                          </Col>
                        )}
                      </Row>
                    </Card.Body>
                  </Card>
                );
              })()}

              <h6 className="fw-bold text-primary mt-4 mb-2">{t('products_in_order', 'Productos en el Pedido')}</h6>
              <Table striped bordered hover responsive size="sm">
                <thead style={{ backgroundColor: '#183eeb', color: 'white' }}>
                  <tr>
                    <th>SKU</th>
                    <th>{t('th_name', 'Producto')}</th>
                    <th>{t('th_unit', 'Unidad')}</th>
                    <th>{t('th_requested_qty', 'Cant. Solicitada')}</th>
                    <th>{t('th_weighed_qty', 'Cant. Pesada')}</th>
                    <th>{t('th_unit_price', 'Precio Unit ($)')}</th>
                    <th>{t('th_subtotal', 'Subtotal ($)')}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items?.map(item => (
                    <tr key={item.id}>
                      <td><code>{item.producto?.sku || item.product?.sku || 'N/A'}</code></td>
                      <td>{item.producto?.nombre || item.product?.nombre}</td>
                      <td><Badge bg="secondary">{item.unidad_medida || 'KG'}</Badge></td>
                      <td className="fw-bold">{item.cantidad_solicitada || item.cantidad}</td>
                      <td className="text-success fw-bold">{item.cantidad_pesada !== null ? item.cantidad_pesada : '---'}</td>
                      <td>${parseFloat(item.precio_unitario_usd || 0).toFixed(2)}</td>
                      <td className="fw-bold">${parseFloat(item.subtotal_definitivo_usd || item.subtotal_referencial_usd || item.subtotal || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: isDark ? '#1e293b' : '#f8fafd', borderTop: '2px solid #183eeb' }}>
                    <td colSpan="6" className="text-end fw-bold py-2">
                      {t('th_total_order', 'TOTAL FACTURA ($)')}:
                    </td>
                    <td className="fw-bold py-2 fs-6 text-primary">
                      ${parseFloat(
                        selectedOrder.total_definitivo_usd > 0
                          ? selectedOrder.total_definitivo_usd
                          : (selectedOrder.total_referencial_usd || selectedOrder.totals?.total || selectedOrder.total_base_usd || 0)
                      ).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          ) : (
            <Alert variant="warning">{t('failed_load_order_info', 'No se pudo cargar la información del pedido.')}</Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          {selectedOrder && selectedOrder.estatus_pedido !== 'Cancelado' && (
            <Button variant="outline-danger" onClick={() => openRejectModal(selectedOrder)}>
              <i className="mdi mdi-close-circle me-1"></i> {t('btn_reject_order', 'Rechazar / Cancelar Pedido')}
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowDetails(false)}>{t('btn_close', 'Cerrar')}</Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL CARGA DE PESOS REALES DE BALANZA (TAB 2) */}
      <Modal show={showWeighingModal} onHide={() => setShowWeighingModal(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#183eeb', color: 'white' }}>
          <Modal.Title>{t('modal_weighing_title')} - Pedido #{weighingOrder?.numero_pedido}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {weighingOrder && (
            <div>
              <Table striped bordered hover responsive size="sm">
                <thead className="bg-light">
                  <tr>
                    <th>SKU</th>
                    <th>{t('th_name', 'Producto')}</th>
                    <th>{t('th_unit', 'Unidad')}</th>
                    <th>{t('th_requested_qty', 'Cantidad Solicitada')}</th>
                    <th>{t('th_real_qty_scale', 'Kilos / Cantidad Real (Balanza)')}</th>
                    <th>{t('th_unit_price', 'Precio Unit. ($)')}</th>
                  </tr>
                </thead>
                <tbody>
                  {weighingOrder.items?.map(item => {
                    const um = (item.unidad_medida || item.product?.unidad_medida || item.producto?.unidad_medida || 'UND').toUpperCase();
                    const isWeighable = (um === 'KG' || um === 'KGS' || um === 'KILO' || um === 'KILOS');
                    return (
                      <tr key={item.id}>
                        <td><code>{item.product?.sku || item.producto?.sku || 'N/A'}</code></td>
                        <td>{item.product?.nombre || item.producto?.nombre || 'Producto'}</td>
                        <td><Badge bg={isWeighable ? "primary" : "secondary"}>{um}</Badge></td>
                        <td className="fw-bold">{item.cantidad_solicitada || item.cantidad}</td>
                        <td style={{ width: '200px' }}>
                          {isWeighable ? (
                            <Form.Control 
                              type="number" 
                              step="0.001" 
                              value={editedWeights[item.id] !== undefined ? editedWeights[item.id] : (item.cantidad_solicitada || item.cantidad)}
                              onChange={e => handleWeightInputChange(item.id, e.target.value)}
                              style={{ border: '2px solid #183eeb', fontWeight: 'bold' }}
                            />
                          ) : (
                            <div className="p-1 px-2 border rounded bg-light fw-bold text-center text-dark">
                              {item.cantidad_solicitada || item.cantidad} {um} <small className="text-muted">(Fijo)</small>
                            </div>
                          )}
                        </td>
                        <td>${parseFloat(item.precio_unitario_usd || 0).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowWeighingModal(false)}>{t('btn_cancel', 'Cancelar')}</Button>
          <Button variant="success" onClick={saveWeighedAmounts} disabled={savingWeights}>
            {savingWeights ? <Spinner size="sm" animation="border" /> : t('btn_confirm_weighing', 'Confirmar Pesaje y Recalcular Total Definitivo')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL LIGHTBOX / VISOR DE COMPROBANTE DE PAGO (Doble Soporte: Modo Claro y Modo Oscuro) */}
      {(() => {
        const modalTheme = {
          bodyBg: isDark ? '#111930' : '#f8fafc',
          cardBg: isDark ? '#161e38' : '#ffffff',
          cardBorder: isDark ? '#233058' : '#e2e8f0',
          titleColor: isDark ? '#38bdf8' : '#183eeb',
          boxBg: isDark ? '#0b1329' : '#f1f5f9',
          boxBorder: isDark ? '#1e293b' : '#cbd5e1',
          codeBg: isDark ? '#111930' : '#ffffff',
          codeColor: isDark ? '#fbbf24' : '#183eeb',
          labelColor: isDark ? '#94a3b8' : '#64748b',
          valueColor: isDark ? '#f8fafc' : '#0f172a',
          amountBoxBg: isDark ? 'rgba(16, 185, 129, 0.12)' : '#f0fdf4',
          amountBoxBorder: isDark ? 'rgba(16, 185, 129, 0.35)' : '#bbf7d0',
          amountColor: isDark ? '#34d399' : '#15803d',
          rateBadgeBg: isDark ? '#0284c7' : '#e0f2fe',
          rateBadgeColor: isDark ? '#ffffff' : '#0369a1',
          reconciliationBg: isDark ? 'rgba(56, 189, 248, 0.08)' : '#eff6ff',
          reconciliationBorder: isDark ? 'rgba(56, 189, 248, 0.25)' : '#bfdbfe',
          reconciliationTitle: isDark ? '#38bdf8' : '#1d4ed8',
          dividerColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
          imageWrapperBg: isDark ? '#0b1021' : '#0f172a',
          imageBorder: isDark ? '#233058' : '#cbd5e1',
          footerBg: isDark ? '#161e38' : '#ffffff',
          footerBorder: isDark ? '#233058' : '#e2e8f0',
        };

        return (
          <Modal 
            show={previewVoucher.show} 
            onHide={closeVoucherModal} 
            size="xl" 
            centered
          >
            <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#00024f', color: 'white' }}>
              <Modal.Title className="h5 fw-bold d-flex align-items-center flex-wrap gap-2 text-white">
                <i className="mdi mdi-receipt-text-check me-1"></i>
                <span className="text-white">{t('modal_voucher_title', 'Comprobante de Pago')}</span>
                {previewVoucher.reference && (
                  <span className="badge bg-primary" style={{ fontSize: '0.85rem' }}>
                    Ref: {previewVoucher.reference}
                  </span>
                )}
                {previewVoucher.payment?.estatus && (
                  <Badge 
                    bg={previewVoucher.payment.estatus === 'Validado' ? 'success' : previewVoucher.payment.estatus === 'Rechazado' ? 'danger' : 'warning'} 
                    className="text-uppercase"
                    style={{ fontSize: '0.8rem' }}
                  >
                    {getInvoiceStatusLabel(previewVoucher.payment.estatus)}
                  </Badge>
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-3" style={{ backgroundColor: modalTheme.bodyBg }}>
              <Row className="g-3 align-items-stretch">
                {/* Panel Izquierdo: Información y Conciliación Adaptativo */}
                <Col lg={5} xl={4} className="d-flex flex-column">
                  <div 
                    className="p-3 rounded h-100 shadow-sm d-flex flex-column" 
                    style={{ 
                      backgroundColor: modalTheme.cardBg, 
                      border: `1px solid ${modalTheme.cardBorder}`, 
                      borderRadius: '12px' 
                    }}
                  >
                    <h6 className="text-uppercase fw-bold mb-3 d-flex align-items-center" style={{ color: modalTheme.titleColor, letterSpacing: '0.5px' }}>
                      <i className="mdi mdi-receipt-text-outline me-2 fs-5"></i>
                      {t('payment_summary', 'Detalle de la Transacción')}
                    </h6>

                    {/* Referencia */}
                    <div className="mb-2 p-2 rounded" style={{ backgroundColor: modalTheme.boxBg, border: `1px solid ${modalTheme.boxBorder}` }}>
                      <span className="small d-block mb-1 fw-semibold" style={{ color: modalTheme.labelColor }}>{t('reference', 'Número de Referencia')}:</span>
                      <div className="d-flex align-items-center justify-content-between">
                        <code className="fw-bold fs-6 px-2 py-1 rounded border" style={{ backgroundColor: modalTheme.codeBg, color: modalTheme.codeColor, borderColor: modalTheme.boxBorder }}>
                          {previewVoucher.reference || 'N/A'}
                        </code>
                        {previewVoucher.reference && (
                          <Button 
                            variant={isDark ? "outline-light" : "outline-secondary"} 
                            size="sm" 
                            className="py-0 px-2"
                            style={{ fontSize: '0.75rem' }}
                            onClick={() => copyToClipboard(previewVoucher.reference)}
                          >
                            <i className={`mdi ${copiedRef ? 'mdi-check text-success' : 'mdi-content-copy'}`}></i>
                            <span className="ms-1">{copiedRef ? '¡Copiado!' : 'Copiar'}</span>
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Método y Moneda */}
                    <Row className="g-2 mb-2">
                      <Col xs={6}>
                        <div className="small fw-semibold" style={{ color: modalTheme.labelColor }}>{t('payment_method', 'Método')}:</div>
                        <span className="badge mt-1 px-2 py-1 fw-bold" style={{ backgroundColor: isDark ? '#1e3a8a' : '#dbeafe', color: isDark ? '#93c5fd' : '#1d4ed8' }}>
                          {previewVoucher.paymentMethod || previewVoucher.payment?.metodo_pago || 'Pago Reportado'}
                        </span>
                      </Col>
                      <Col xs={6}>
                        <div className="small fw-semibold" style={{ color: modalTheme.labelColor }}>{t('currency', 'Moneda')}:</div>
                        <strong className="mt-1 d-block" style={{ color: modalTheme.valueColor }}>{previewVoucher.payment?.moneda || 'USD'}</strong>
                      </Col>
                    </Row>

                    {/* Monto y Tasa */}
                    <div className="my-2 p-2 rounded" style={{ backgroundColor: modalTheme.amountBoxBg, border: `1px solid ${modalTheme.amountBoxBorder}` }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="small fw-semibold" style={{ color: modalTheme.labelColor }}>{t('reported_amount', 'Monto Reportado')}:</span>
                        <strong className="fs-5 fw-bold" style={{ color: modalTheme.amountColor }}>
                          {previewVoucher.payment?.moneda || 'USD'} {Number(previewVoucher.payment?.monto_reportado ?? previewVoucher.payment?.monto ?? 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </strong>
                      </div>
                      {previewVoucher.payment?.tasa_cambio && (
                        <div className="d-flex justify-content-between align-items-center small mb-1" style={{ color: modalTheme.labelColor }}>
                          <span className="fw-medium">{t('applied_rate', 'Tasa Aplicada')}:</span>
                          <span className="badge fw-bold px-2 py-1" style={{ backgroundColor: modalTheme.rateBadgeBg, color: modalTheme.rateBadgeColor }}>
                            {Number(previewVoucher.payment.tasa_cambio).toFixed(4)} Bs/$
                          </span>
                        </div>
                      )}
                      {previewVoucher.payment?.monto_equivalente_usd && (
                        <div className="d-flex justify-content-between align-items-center small pt-1 border-top" style={{ borderColor: modalTheme.amountBoxBorder }}>
                          <span className="fw-semibold" style={{ color: modalTheme.labelColor }}>{t('usd_equivalent', 'Equivalente USD')}:</span>
                          <strong className="fw-bold fs-6" style={{ color: modalTheme.valueColor }}>
                            ${Number(previewVoucher.payment.monto_equivalente_usd).toFixed(2)} USD
                          </strong>
                        </div>
                      )}
                    </div>

                    {/* Banco Destino */}
                    {previewVoucher.payment?.banco_destino && (
                      <div className="mb-2 small d-flex justify-content-between">
                        <span className="fw-semibold" style={{ color: modalTheme.labelColor }}>{t('target_bank', 'Banco Destino')}:</span>
                        <strong style={{ color: modalTheme.valueColor }}>{previewVoucher.payment.banco_destino}</strong>
                      </div>
                    )}

                    {/* Fecha */}
                    {previewVoucher.payment?.created_at && (
                      <div className="mb-2 small d-flex justify-content-between">
                        <span className="fw-semibold" style={{ color: modalTheme.labelColor }}>{t('report_date', 'Fecha de Reporte')}:</span>
                        <span style={{ color: modalTheme.valueColor }}>{new Date(previewVoucher.payment.created_at).toLocaleString()}</span>
                      </div>
                    )}

                    {/* Detalles de Conciliación Bancaria */}
                    {previewVoucher.payment?.detalles_pago && Object.keys(previewVoucher.payment.detalles_pago).length > 0 && (
                      <div className="mt-auto p-2 rounded" style={{ backgroundColor: modalTheme.reconciliationBg, border: `1px solid ${modalTheme.reconciliationBorder}` }}>
                        <div className="small fw-bold mb-2 d-flex align-items-center" style={{ color: modalTheme.reconciliationTitle }}>
                          <i className="mdi mdi-bank-transfer me-1 fs-5"></i>
                          {t('reconciliation_data', 'Datos de Conciliación Bancaria')}
                        </div>
                        <div className="small" style={{ lineHeight: '1.6' }}>
                          {previewVoucher.payment.detalles_pago.banco_origen && (
                            <div className="d-flex justify-content-between border-bottom pb-1 mb-1" style={{ borderColor: modalTheme.dividerColor }}>
                              <span className="fw-medium" style={{ color: modalTheme.labelColor }}>Banco Origen:</span>
                              <strong style={{ color: modalTheme.valueColor }}>{previewVoucher.payment.detalles_pago.banco_origen}</strong>
                            </div>
                          )}
                          {previewVoucher.payment.detalles_pago.telefono_emisor && (
                            <div className="d-flex justify-content-between border-bottom pb-1 mb-1" style={{ borderColor: modalTheme.dividerColor }}>
                              <span className="fw-medium" style={{ color: modalTheme.labelColor }}>Teléfono Emisor:</span>
                              <strong style={{ color: modalTheme.valueColor }}>{previewVoucher.payment.detalles_pago.telefono_emisor}</strong>
                            </div>
                          )}
                          {previewVoucher.payment.detalles_pago.cedula_emisor && (
                            <div className="d-flex justify-content-between border-bottom pb-1 mb-1" style={{ borderColor: modalTheme.dividerColor }}>
                              <span className="fw-medium" style={{ color: modalTheme.labelColor }}>Cédula/RIF Emisor:</span>
                              <strong style={{ color: modalTheme.valueColor }}>{previewVoucher.payment.detalles_pago.cedula_emisor}</strong>
                            </div>
                          )}
                          {previewVoucher.payment.detalles_pago.titular_cuenta && (
                            <div className="d-flex justify-content-between border-bottom pb-1 mb-1" style={{ borderColor: modalTheme.dividerColor }}>
                              <span className="fw-medium" style={{ color: modalTheme.labelColor }}>Titular:</span>
                              <strong style={{ color: modalTheme.valueColor }}>{previewVoucher.payment.detalles_pago.titular_cuenta}</strong>
                            </div>
                          )}
                          {previewVoucher.payment.detalles_pago.email_emisor && (
                            <div className="d-flex justify-content-between">
                              <span className="fw-medium" style={{ color: modalTheme.labelColor }}>Email:</span>
                              <strong style={{ color: modalTheme.valueColor }}>{previewVoucher.payment.detalles_pago.email_emisor}</strong>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Col>

                {/* Panel Derecho: Visor de Imagen */}
                <Col lg={7} xl={8}>
                  <div 
                    className="d-flex justify-content-center align-items-center p-2 rounded h-100" 
                    style={{ 
                      backgroundColor: modalTheme.imageWrapperBg, 
                      minHeight: '400px', 
                      maxHeight: '72vh', 
                      overflow: 'auto', 
                      border: `1px solid ${modalTheme.imageBorder}`, 
                      borderRadius: '12px' 
                    }}
                  >
                    {previewVoucher.url?.toLowerCase().includes('.pdf') ? (
                      <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center p-4 text-center" style={{ minHeight: '380px' }}>
                        <i className="mdi mdi-file-pdf-box text-danger mb-3" style={{ fontSize: '64px' }}></i>
                        <h5 className="fw-bold mb-2">Comprobante en Formato PDF</h5>
                        <p className="text-muted small mb-3">Haga clic a continuación para visualizar o descargar el comprobante en una nueva pestaña.</p>
                        <Button 
                          variant="danger" 
                          onClick={() => window.open(previewVoucher.url, '_blank')}
                          className="fw-bold shadow-sm px-4 py-2"
                        >
                          <i className="mdi mdi-open-in-new me-1"></i> Abrir Documento PDF
                        </Button>
                      </div>
                    ) : (
                      <img 
                        src={previewVoucher.url} 
                        alt="Comprobante de pago" 
                        className="img-fluid rounded shadow"
                        style={{ maxHeight: '68vh', maxWidth: '100%', objectFit: 'contain' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://placehold.co/600x400?text=Comprobante+No+Disponible';
                        }}
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ backgroundColor: modalTheme.footerBg, borderTop: `1px solid ${modalTheme.footerBorder}` }}>
              <div>
                {previewVoucher.url && (
                  <Button 
                    variant={isDark ? "outline-info" : "outline-primary"} 
                    size="sm"
                    onClick={() => window.open(previewVoucher.url, '_blank')}
                    className="fw-bold"
                  >
                    <i className="mdi mdi-open-in-new me-1"></i> {previewVoucher.url?.toLowerCase().includes('.pdf') ? 'Abrir PDF en pestaña nueva' : t('open_in_new_tab', 'Abrir imagen en pestaña nueva')}
                  </Button>
                )}
              </div>
              <div className="d-flex align-items-center gap-2">
                {previewVoucher.payment?.estatus !== 'Validado' && previewVoucher.payment?.estatus !== 'Rechazado' && previewVoucher.order && (
                  <>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      className="fw-bold"
                      onClick={() => openRejectPaymentModal(previewVoucher.order, previewVoucher.payment)}
                    >
                      <i className="mdi mdi-close-circle-outline me-1"></i> {t('btn_reject_payment', 'Rechazar Pago')}
                    </Button>
                    <Button 
                      variant="success" 
                      size="sm"
                      className="fw-bold"
                      onClick={async () => {
                        await updateStatus(previewVoucher.order.id, 'Aprobado');
                        setPreviewVoucher(prev => ({
                          ...prev,
                          payment: prev.payment ? { ...prev.payment, estatus: 'Validado' } : null
                        }));
                      }}
                    >
                      <i className="mdi mdi-check-circle me-1"></i> {t('btn_approve_payment', 'Aprobar Pago')}
                    </Button>
                  </>
                )}
                <Button variant="secondary" size="sm" onClick={closeVoucherModal}>
                  {t('btn_close', 'Cerrar')}
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        );
      })()}

      {/* MODAL RECHAZO DE PAGO CON MAESTRO AUTOGESTIONABLE (HU27) */}
      <Modal 
        show={rejectPaymentModal.show} 
        onHide={closeRejectPaymentModal} 
        centered
        size="lg"
      >
        <Form onSubmit={handleConfirmRejectPayment}>
          <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: '#dc3545', color: '#ffffff' }}>
            <Modal.Title className="h5 fw-bold d-flex align-items-center text-white">
              <i className="mdi mdi-close-octagon me-2"></i>
              <span>{t('reject_payment_title', 'Rechazar Comprobante de Pago')}</span>
              {rejectPaymentModal.order?.numero_pedido && (
                <span className="badge bg-light text-danger ms-2" style={{ fontSize: '0.85rem' }}>
                  Pedido: {rejectPaymentModal.order.numero_pedido}
                </span>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}>
            {/* Alerta explicativa */}
            <Alert variant="warning" className="d-flex align-items-start gap-2 py-2 px-3 mb-3 border-0 shadow-sm" style={{ borderRadius: '10px' }}>
              <i className="mdi mdi-alert fs-4 text-warning mt-n1"></i>
              <div style={{ fontSize: '0.88rem' }}>
                <strong>{t('notice_important', 'Información Importante:')}</strong> {t('reject_payment_notice', 'Al rechazar este comprobante, el pedido NO se cancelará ni se devolverá el stock. El pedido regresará a estado "Pendiente por Cobrar" y se notificará automáticamente al cliente vía Push móvil y Correo para que vuelva a reportar un pago válido.')}
              </div>
            </Alert>

            {/* Listado de motivos del catálogo maestro */}
            <div className="mb-3">
              <label className="fw-bold mb-2 d-flex align-items-center justify-content-between" style={{ color: isDark ? '#ffffff' : '#010028' }}>
                <span>
                  <i className="mdi mdi-format-list-checks me-1 text-danger"></i>
                  {t('select_rejection_reasons', 'Seleccione el o los motivos del rechazo (Obligatorio):')} *
                </span>
                <small className="text-muted fw-normal">
                  {selectedRejectReasons.length} seleccionado(s)
                </small>
              </label>

              {loadingRejectReasons ? (
                <div className="text-center py-3">
                  <Spinner size="sm" animation="border" variant="danger" />
                  <span className="ms-2 small text-muted">Cargando motivos...</span>
                </div>
              ) : (
                <div 
                  className="p-2 rounded border" 
                  style={{ 
                    maxHeight: '230px', 
                    overflowY: 'auto', 
                    backgroundColor: isDark ? '#10172d' : '#f8f9fa',
                    borderColor: isDark ? '#233052' : '#e2e8f0' 
                  }}
                >
                  {rejectReasonsCatalog.map(reason => {
                    const isChecked = selectedRejectReasons.includes(reason.value);
                    return (
                      <div 
                        key={reason.id} 
                        className={`d-flex align-items-center p-2 rounded mb-1 transition-all ${isChecked ? 'bg-danger bg-opacity-10 border border-danger' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleRejectReason(reason.value)}
                      >
                        <Form.Check 
                          type="checkbox"
                          id={`reason-${reason.id}`}
                          checked={isChecked}
                          onChange={() => {}} // handled by parent div onClick
                          className="me-2"
                        />
                        <label 
                          htmlFor={`reason-${reason.id}`} 
                          className="mb-0 flex-grow-1" 
                          style={{ cursor: 'pointer', fontSize: '0.9rem', color: isDark ? '#f8fafc' : '#0f172a' }}
                        >
                          <strong>{reason.value}</strong>
                          <span className="text-muted small ms-2">({reason.name})</span>
                        </label>
                      </div>
                    );
                  })}
                  {rejectReasonsCatalog.length === 0 && (
                    <div className="text-center py-3 text-muted small">
                      No se encontraron motivos activos en el catálogo maestro.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Observaciones adicionales */}
            <Form.Group className="mb-2">
              <Form.Label className="fw-bold d-flex align-items-center" style={{ color: isDark ? '#ffffff' : '#010028' }}>
                <i className="mdi mdi-comment-text-outline me-1"></i>
                {t('rejection_notes_optional', 'Observación o Nota Adicional (Opcional):')}
              </Form.Label>
              <Form.Control 
                as="textarea" 
                rows={2} 
                placeholder="Ej: Faltan $15 USD para completar el total, o la foto está borrosa en la fecha."
                value={rejectObservations}
                onChange={e => setRejectObservations(e.target.value)}
                style={{ 
                  borderRadius: '8px', 
                  backgroundColor: isDark ? '#10172d' : '#ffffff',
                  color: isDark ? '#f8fafc' : '#010028',
                  borderColor: isDark ? '#233052' : '#cbd5e1'
                }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ backgroundColor: isDark ? '#10172d' : '#f8f9fa', borderTop: isDark ? '1px solid #233052' : '1px solid #e2e8f0' }}>
            <Button variant="secondary" onClick={closeRejectPaymentModal} style={{ borderRadius: '8px' }}>
              {t('btn_cancel', 'Cancelar')}
            </Button>
            <Button 
              type="submit" 
              variant="danger" 
              disabled={savingRejectPayment || selectedRejectReasons.length === 0} 
              className="fw-bold" 
              style={{ borderRadius: '8px' }}
            >
              {savingRejectPayment ? (
                <>
                  <Spinner size="sm" animation="border" className="me-1" />
                  {t('processing', 'Rechazando...')}
                </>
              ) : (
                <>
                  <i className="mdi mdi-close-circle-outline me-1"></i>
                  {t('btn_confirm_reject_payment', 'Confirmar Rechazo y Notificar Cliente')}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default Orders;